import { useState } from 'react'
import { UseFieldArrayAppend, UseFieldArrayRemove, UseFormReturn } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { t } from 'i18next'

import { AddActionDialogType } from '@pages/EntityCreateOrEdit'

import { useFetchDictionariesByCategoryCodeQuery } from '@redux/api'
import {
  useCreateTooltipMutation,
  useDeleteTooltipActionMutation,
  useDeleteTooltipPointMutation,
  useUpdateTooltipActionMutation,
  useUpdateTooltipJSONMutation,
  useUpdateTooltipMutation,
  useUpdateTooltipPointMutation,
} from '@redux/api/tooltips.api'

import { CATEGORIES, OPERATION_TYPE, ROUTES, TIMELINE_ACTION_COLUMNS } from '@constants'
import { ActionRow, GETShareColumn, ModalType, TooltipVariableRowType } from '@types'

import { TooltipForm } from '../types'

type useHandlersParams = {
  id: number | null
  methods: UseFormReturn<TooltipForm>
  append: UseFieldArrayAppend<any, any>
  remove: UseFieldArrayRemove
}

export const useHandlers = ({ id, methods, append, remove }: useHandlersParams) => {
  const navigate = useNavigate()
  const { handleSubmit, getValues, watch, setValue } = methods

  const [currentTab, setCurrentTab] = useState<number>(0)
  const [currentRowId, setCurrentRowId] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isOpenDialog, setIsOpenDialog] = useState<boolean>(false)
  const [isShowScriptValueDialogJson, setShowScriptValueDialogJson] = useState<boolean>(false)
  const [isShowDeleteVariableModal, setIsShowDeleteVariableModal] = useState<boolean>(false)
  const [updatedVariable, setUpdatedVariable] = useState<TooltipVariableRowType | null>(null)

  const [showAddActionDialog, setShowAddActionDialog] = useState(false)
  const [addActionDialogData, setAddActionDialogData] =
    useState<
      Omit<
        AddActionDialogType,
        'createAction' | 'editAction' | 'showDialog' | 'onCloseModal' | 'actionEventsCategoryCode'
      >
    >()

  const [createTooltip, { isLoading: isLoadingCreateTooltip }] = useCreateTooltipMutation()
  const [updateTooltip, { isLoading: isLoadingUpdateTooltip }] = useUpdateTooltipMutation()
  const [updateTooltipJson, { isLoading: isLoadingTooltipJSON }] = useUpdateTooltipJSONMutation()
  const [updateTooltipPoint, { isLoading: isLoadingTooltipPoint }] = useUpdateTooltipPointMutation()
  const [deleteTooltipAction] = useDeleteTooltipActionMutation()
  const [updateTooltipAction, { isLoading: isUpdateActionProcessing }] =
    useUpdateTooltipActionMutation()
  const [deleteTooltipPoint] = useDeleteTooltipPointMutation()
  const { data: bindingValuesAction } = useFetchDictionariesByCategoryCodeQuery(
    CATEGORIES.BINDING_TOOLTIP_ACTION
  )

  const watchedActions = watch('actions')
  const watchedObject = watch('object')

  const handleSave = handleSubmit((tooltipData: TooltipForm) => {
    const data = {
      id: tooltipData.id ?? null,
      code: tooltipData.code,
      title: tooltipData.title,
      objectCode: tooltipData.object!.id,
      objectTitle: tooltipData.object!.label,
      width: tooltipData.width,
    }

    setIsLoading(true)

    if (tooltipData.id) {
      updateTooltip(data).finally(() => setIsLoading(false))
    } else {
      createTooltip(data)
        .unwrap()
        .then(res => navigate(`${ROUTES.CONFIG_TOOLTIPS_EDIT}/${res.id}`))
        .finally(() => setIsLoading(false))
    }
  })

  const handleCancel = () => navigate(`${ROUTES.CONFIG_TOOLTIPS}`)

  const handleTabChange = (event: React.SyntheticEvent, value: number) => setCurrentTab(value)

  const handleChangeJsonMode = (value: boolean) => updateTooltipJson({ id, isJson: value })

  const handleChangeValueScriptJson = (value: string) => {
    updateTooltipJson({
      id,
      isJson: true,
      jsonValue: value,
    })
  }

  const handleOpenPointDialog = () => setIsOpenDialog(true)

  const handleClosePointDialog = () => {
    setIsOpenDialog(false)
    setCurrentRowId(null)
  }

  const handleSavePoint = (data: any) => {
    updateTooltipPoint({
      id: currentRowId ?? null,
      tooltipId: id,
      title: data.title,
      bindType: data.bindType.id,
      value: data.value,
      background: data.background,
      conditionsHidden: data.conditionsHidden,
      valueType: data.valueType ? data.valueType : null,
      formatDate: data?.formatDate && data.formatDate?.label ? data.formatDate.label : null,
    }).then(() => handleClosePointDialog())
  }

  const handleEditPoint = (params: any) => {
    setIsOpenDialog(true)
    setCurrentRowId(params.row.original.id)
  }

  const handleDeletePoint = (id: number | string) => deleteTooltipPoint(id)

  const handleAddVariableItem = () => {
    append({ field: '', pathStr: '', pathArray: [] })
  }

  const handleSaveVariable = (variable: TooltipVariableRowType) => {
    const { id, code, title, object } = getValues()
    const { id: variableId, order, field, pathStr, pathArray } = variable

    updateTooltip({
      id,
      code,
      title,
      objectCode: object!.id,
      objectTitle: object!.label,
      updateTooltipVariables: [
        {
          operationType: variableId ? OPERATION_TYPE.UPDATE : OPERATION_TYPE.CREATE,
          type: 'VARIABLE',
          data: {
            id: variableId ?? null,
            tooltipId: id,
            order,
            field,
            pathStr,
            pathArray,
          },
        },
      ],
    })
  }

  const handleDeleteVariable = () => {
    const { id, code, title, object, variables } = getValues()

    const findIndex = variables?.findIndex(el => el.id === updatedVariable?.id)

    if (findIndex === -1) {
      return
    }

    updateTooltip({
      id,
      code,
      title,
      objectCode: object!.id,
      objectTitle: object!.label,
      updateTooltipVariables: [
        {
          operationType: OPERATION_TYPE.DELETE,
          type: 'VARIABLE',
          data: {
            id: updatedVariable?.id,
            tooltipId: id,
            order: updatedVariable?.order,
            field: updatedVariable?.field,
            pathStr: updatedVariable?.pathStr,
            pathArray: updatedVariable?.pathArray,
          },
        },
      ],
    }).then(() => setIsShowDeleteVariableModal(false))

    remove(findIndex)
  }

  const handleOpenDeleteVariableModal = (variable: TooltipVariableRowType, index: number) => {
    if (variable.id) {
      setIsShowDeleteVariableModal(true)
      setUpdatedVariable(variable)

      return
    }

    remove(index)
  }

  const handleCloseDeleteVariableModal = () => {
    setIsShowDeleteVariableModal(false)
  }

  const handleCloseAddActionDialog = (isDirty?: boolean) => {
    if (!isDirty) {
      setShowAddActionDialog(false)
      setAddActionDialogData(undefined)

      return
    }

    if (confirm(t('notifications.leave'))) {
      setShowAddActionDialog(false)
      setAddActionDialogData(undefined)
    }
  }

  const handleOpenAddActionDialog = (modalType: ModalType, id?: string | number) => {
    const actionData = watchedActions?.find(item => item.id === id)

    setShowAddActionDialog(true)
    setAddActionDialogData({
      modalType,
      bindingValuesAction,
      objectCode: watchedObject?.id as string,
      currentRow: actionData as GETShareColumn,
      actionTable: {
        columns: TIMELINE_ACTION_COLUMNS,
        rows: watchedActions,
      },
      columns: [],
    })
  }

  const handleChangeAction = (data: ActionRow) => {
    return updateTooltipAction({ ...data, objectTooltipId: id as number }).then(resp => {
      const respData = resp.data

      const columnIndex = watchedActions?.findIndex(item => item.id === respData.id)

      if (columnIndex && columnIndex < 0) {
        setValue('actions', watchedActions.concat(respData))

        return
      }

      watchedActions[columnIndex] = respData
      setValue('actions', watchedActions)
    })
  }

  const handleDeleteAction = (id: string | number) => {
    deleteTooltipAction(id as number).then(() => {
      setValue(
        'actions',
        watchedActions.filter(item => item.id !== id)
      )
    })
  }

  return {
    data: {
      watchedActions,
      isLoadingTooltip:
        isLoadingCreateTooltip ||
        isLoadingUpdateTooltip ||
        isLoadingTooltipJSON ||
        isLoadingTooltipPoint,
      isUpdateActionProcessing,
    },
    state: {
      currentTab,
      currentRowId,
      isOpenDialog,
      isShowScriptValueDialogJson,
      isShowDeleteVariableModal,
      isLoading,
      showAddActionDialog,
      addActionDialogData,
    },
    handlers: {
      handleSave,
      handleCancel,
      handleTabChange,
      handleChangeJsonMode,
      handleChangeValueScriptJson,
      handleOpenPointDialog,
      handleClosePointDialog,
      handleSavePoint,
      handleEditPoint,
      handleDeletePoint,
      handleAddVariableItem,
      handleSaveVariable,
      handleDeleteVariable,
      handleOpenDeleteVariableModal,
      handleCloseDeleteVariableModal,
      setShowScriptValueDialogJson,
      handleDeleteAction,
      handleOpenAddActionDialog,
      handleCloseAddActionDialog,
      handleChangeAction,
    },
  }
}
